import {
  ACTION,
  BlotterTable,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
  Flex,
  getShowJSONContextItem,
  MixpanelEvent,
  MixpanelEventSource,
  Panel,
  PANEL_HEADER_HEIGHT,
  PanelActions,
  PanelContent,
  PanelHeader,
  Text,
  useBlotterTable,
  useDynamicCallback,
  useJsonModal,
  useMixpanel,
  usePersistedBlotterTable,
  usePortal,
  type IHedgeRule,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { useCallback, useState } from 'react';

import {
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  FormControlSizes,
  LedgerAccountTypeEnum,
  useGetDefaultContextMenuItems,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { usePositionAutoHedgingRulesDrawer } from '../../../../components/AutoHedgingRulesDrawer/AutoHedgingRulesDrawer';
import { useFeatureFlag, useRoleAuth } from '../../../../hooks';
import { useGetOpenTransactionsMenuItems } from '../AccountLedgerEvents/useGetOpenTransactionsMenuItems';
import { useHedgeRuleControlsColumns } from './columns';
import { usePositionAutoHedgingRules } from './usePositionAutoHedgingRules';

export function useGetPositionAutoHedgingRulesMenu() {
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();
  const { handleClickJson, jsonModal } = useJsonModal();
  const { isAuthorized } = useRoleAuth();
  const { enableAccountLedgerEventsPage } = useFeatureFlag();

  const getOpenTransactionsMenuItems = useGetOpenTransactionsMenuItems();

  const getAdditionalMenuItems = useCallback(
    (params: GetContextMenuItemsParams<IHedgeRule>) => {
      const items: (MenuItemDef | null)[] = [];
      const hedgeRule = params.node?.data;
      const hedgeRuleSubAccountName =
        hedgeRule && params.context.current.subAccountsByID?.get(hedgeRule.AccountID)?.Name;
      if (
        isAuthorized(ACTION.VIEW_LEDGER_EVENTS) &&
        enableAccountLedgerEventsPage &&
        hedgeRule &&
        hedgeRuleSubAccountName
      ) {
        items.push(
          ...getOpenTransactionsMenuItems({
            asset: hedgeRule.Asset,
            account: hedgeRuleSubAccountName,
            accountType: LedgerAccountTypeEnum.SubAccount,
            source: MixpanelEventSource.PositionAutoHedging,
          })
        );
      }
      return compact(items);
    },
    [enableAccountLedgerEventsPage, isAuthorized, getOpenTransactionsMenuItems]
  );

  const getContextMenuItems = useDynamicCallback((params: GetContextMenuItemsParams<IHedgeRule>) => {
    return [
      ...getAdditionalMenuItems(params),
      ...getDefaultContextMenuItems(params),
      getShowJSONContextItem({ params, handleClickJson }),
    ];
  });

  return {
    getContextMenuItems,
    modals: [jsonModal],
  };
}

export function PositionAutoHedgingRules({ tabId }: { tabId: string }) {
  const mixpanel = useMixpanel();
  const cols = useHedgeRuleControlsColumns();
  const { getContextMenuItems, modals } = useGetPositionAutoHedgingRulesMenu();
  const [quickFilterText, setQuickFilterText] = useState<string>('');
  const { openPositionAutoHedgingRulesDrawer } = usePositionAutoHedgingRulesDrawer();
  const persistedBlotterTable = usePersistedBlotterTable(`PAH-Tab/${tabId}`);

  const { dataObs, filterBuilderAccordion } = usePositionAutoHedgingRules({
    onFilterChanged: persistedBlotterTable.onFilterChanged,
    initialState: persistedBlotterTable.initialState,
  });
  const blotterTable = useBlotterTable({
    dataObservable: dataObs,
    errorObservable: undefined,
    rowID: 'HedgeRuleID' as keyof IHedgeRule,
    quickSearchParams: {
      filterText: quickFilterText,
    },
    persistence: persistedBlotterTable,
    columns: cols,
    gridOptions: {
      onRowDoubleClicked: ({ data }) =>
        data != null && openPositionAutoHedgingRulesDrawer({ hedgeRuleID: data.HedgeRuleID }),
      rowSelection: DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
      getContextMenuItems,
    },
  });
  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <>
      <Panel data-testid="position-auto-hedging-rules">
        <PanelHeader minHeight={PANEL_HEADER_HEIGHT / 2 + 'px'} px="spacing16">
          <Flex gap="spacing12" alignItems="flex-end">
            <h2>Rules</h2>
            <Text fontSize="fontSizeSmall">Position Autohedging</Text>
          </Flex>

          <PanelActions>
            <Box ref={filtersContainerRef} />
            <Button
              data-testid="create-rule-button"
              variant={ButtonVariants.Positive}
              size={FormControlSizes.Small}
              onClick={() => {
                mixpanel.track(MixpanelEvent.ClickCreateNewAutohedgingRule);
                openPositionAutoHedgingRulesDrawer();
              }}
            >
              Create Rule
            </Button>
          </PanelActions>
        </PanelHeader>
        <PanelContent pb={0} px="spacing16">
          <BlotterTableFilters {...filterBuilderAccordion} onQuickFilterTextChanged={setQuickFilterText} />
          <BlotterTable {...blotterTable} />
        </PanelContent>
      </Panel>
      {modals}
    </>
  );
}
